import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Reply Tracker — the third Company Tool on the Resources page (beside the CRM
// and Key Contacts). Log an important email you've sent and are waiting on a
// reply for; optionally attach it to a CRM lead. Once the reply window passes
// with no reply, it nudges you in the My Day "Needs follow-up" box. Manual MVP:
// you record the send + mark the reply (Outlook auto-detect comes later).
import { notify } from "../Toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import MultiSelect from "../MultiSelect";
const LS_OPEN = "reply-tracker-open";
// "Thu, May 28" — short local date.
function shortDate(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return "";
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
}
// Whole-day delta from now to the due date, used for the "due in / overdue by"
// label. Positive = days remaining, negative = days overdue.
function daysFromNow(iso) {
    const due = new Date(iso).getTime();
    const now = Date.now();
    return Math.round((due - now) / 86_400_000);
}
function dueLabel(e) {
    const d = daysFromNow(e.reply_due_at);
    if (e.overdue) {
        const od = Math.max(1, -d);
        return `Overdue · ${od} day${od === 1 ? "" : "s"} no reply`;
    }
    if (d <= 0)
        return "Reply due today";
    return `Reply due in ${d} day${d === 1 ? "" : "s"}`;
}
export default function ReplyTrackerBoard() {
    const [board, setBoard] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(() => {
        try {
            // Default open — the user clicked into this tool to look at it.
            return localStorage.getItem(LS_OPEN) !== "0";
        }
        catch {
            return true;
        }
    });
    const [creating, setCreating] = useState(false);
    const [showClosed, setShowClosed] = useState(false);
    // Pin to the Resources Quick-access strip (mirrors the CRM / Key Contacts
    // pins): write localStorage + fire an event the Resources page listens for.
    const [pinned, setPinned] = useState(() => {
        try {
            return localStorage.getItem("reply-tracker-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    function togglePinned() {
        setPinned((p) => {
            const next = !p;
            try {
                localStorage.setItem("reply-tracker-pinned", next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            window.dispatchEvent(new Event("reply-tracker:pin-changed"));
            return next;
        });
    }
    const load = useCallback(async () => {
        try {
            setBoard(await api.get("/reply-tracker/board"));
        }
        catch {
            /* non-fatal */
        }
        finally {
            setLoaded(true);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    function toggleOpen() {
        setOpen((o) => {
            const next = !o;
            try {
                localStorage.setItem(LS_OPEN, next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            return next;
        });
    }
    const waiting = useMemo(() => (board?.items ?? []).filter((e) => e.status === "waiting"), [board]);
    const closed = useMemo(() => (board?.items ?? []).filter((e) => e.status !== "waiting"), [board]);
    const overdueCount = useMemo(() => waiting.filter((e) => e.overdue).length, [waiting]);
    async function markReplied(e) {
        try {
            await api.post(`/reply-tracker/${e.id}/replied`, {});
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't update");
        }
    }
    async function snooze(e, days) {
        try {
            await api.post(`/reply-tracker/${e.id}/snooze`, { days });
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't snooze");
        }
    }
    async function remove(e) {
        if (!confirm(`Stop tracking "${e.subject}"?`))
            return;
        try {
            await api.del(`/reply-tracker/${e.id}`);
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't delete");
        }
    }
    if (!loaded || !board)
        return null;
    return (_jsxs("div", { className: "rt-board", children: [_jsxs("button", { type: "button", className: "rt-board-header", onClick: toggleOpen, "aria-expanded": open, children: [_jsx("span", { className: "rt-board-title", children: "\uD83D\uDCE8 Reply Tracker" }), _jsxs("span", { className: "rt-board-summary muted small", children: [waiting.length, " waiting", overdueCount > 0 ? ` · ${overdueCount} overdue` : ""] }), _jsx("span", { className: "rt-board-caret", "aria-hidden": "true", children: open ? "▾" : "▸" })] }), open && (_jsxs("div", { className: "rt-board-body", children: [_jsxs("p", { className: "muted small rt-intro", children: ["Log an important email you've sent. If you don't get a reply within your wait window, it nudges you in the My Day", " ", _jsx("b", { children: "\"Needs follow-up\"" }), " box \u2014 no calendar block, just a reminder."] }), _jsxs("div", { className: "rt-toolbar", children: [_jsx("button", { type: "button", className: "btn-primary rt-add-btn", onClick: () => setCreating(true), children: "+ Log an email" }), _jsx("button", { type: "button", className: "rt-btn", onClick: togglePinned, title: "Pin Reply Tracker to Quick access", children: pinned ? "📌 Pinned" : "📌 Pin" }), _jsx("span", { className: "muted small rt-soon", children: "Outlook auto-detect coming soon" })] }), waiting.length === 0 ? (_jsx("p", { className: "muted small rt-empty", children: "Nothing waiting on a reply. Log an email to start tracking." })) : (_jsx("ul", { className: "rt-list", children: waiting.map((e) => (_jsxs("li", { className: `rt-item ${e.overdue ? "overdue" : ""}`, children: [_jsxs("div", { className: "rt-item-info", children: [_jsx("div", { className: "rt-item-subject", children: e.subject }), _jsxs("div", { className: "rt-item-meta muted", children: [e.recipient ? `To ${e.recipient}` : "Awaiting reply", e.lead_name ? (_jsxs("span", { className: "rt-lead-tag", children: ["\uD83C\uDFAF ", e.lead_name] })) : null, _jsx("span", { className: "rt-sep", children: "\u00B7" }), "Sent ", shortDate(e.sent_at)] }), _jsx("div", { className: `rt-item-due ${e.overdue ? "overdue" : ""}`, children: dueLabel(e) }), e.assignee_names.length > 0 && (_jsxs("div", { className: "rt-item-assignees muted small", children: ["\uD83D\uDC64 ", e.assignee_names.join(", ")] }))] }), _jsxs("div", { className: "rt-item-actions", children: [_jsx("button", { type: "button", className: "rt-btn primary", onClick: () => markReplied(e), title: "They replied \u2014 clear it", children: "\u2713 Replied" }), _jsx("button", { type: "button", className: "rt-btn", onClick: () => snooze(e, 2), title: "Wait 2 more days", children: "Snooze 2d" }), _jsx("button", { type: "button", className: "rt-btn ghost", onClick: () => remove(e), title: "Stop tracking", children: "\u2715" })] })] }, e.id))) })), closed.length > 0 && (_jsxs("div", { className: "rt-closed", children: [_jsxs("button", { type: "button", className: "rt-closed-toggle muted small", onClick: () => setShowClosed((s) => !s), children: [showClosed ? "▾" : "▸", " ", closed.length, " replied / closed"] }), showClosed && (_jsx("ul", { className: "rt-list rt-list-closed", children: closed.map((e) => (_jsxs("li", { className: "rt-item closed", children: [_jsxs("div", { className: "rt-item-info", children: [_jsx("div", { className: "rt-item-subject", children: e.subject }), _jsxs("div", { className: "rt-item-meta muted", children: [e.recipient ? `To ${e.recipient}` : "", e.lead_name ? (_jsxs("span", { className: "rt-lead-tag", children: ["\uD83C\uDFAF ", e.lead_name] })) : null, e.replied_at ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "rt-sep", children: "\u00B7" }), "Replied ", shortDate(e.replied_at)] })) : null] })] }), _jsx("div", { className: "rt-item-actions", children: _jsx("button", { type: "button", className: "rt-btn ghost", onClick: () => remove(e), title: "Remove from history", children: "\u2715" }) })] }, e.id))) }))] }))] })), creating && (_jsx(LogEmailModal, { leads: board.leads, onClose: () => setCreating(false), onSaved: async () => {
                    setCreating(false);
                    await load();
                } }))] }));
}
// Log a sent email to track. The lead dropdown attaches it to a CRM lead so
// the nudge (and the closed-history row) shows which deal it belongs to.
function LogEmailModal({ leads, onClose, onSaved, }) {
    const [subject, setSubject] = useState("");
    const [recipient, setRecipient] = useState("");
    const [leadId, setLeadId] = useState("");
    const [sentDate, setSentDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [waitDays, setWaitDays] = useState(3);
    // When the user picks "Other…" from the preset list, we reveal a free
    // numeric input (1–90 days) and read the day count from `customDays`.
    const [customWait, setCustomWait] = useState(false);
    const [customDays, setCustomDays] = useState(4);
    const [notes, setNotes] = useState("");
    // Assignees — the only people who'll see the tracked email + get the nudge.
    // Not pre-filled with the creator: you assign whoever should chase it.
    const [assigneeIds, setAssigneeIds] = useState([]);
    const [coworkers, setCoworkers] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                setCoworkers(await api.get("/users/coworkers?include_self=true"));
            }
            catch {
                /* non-fatal — picker just shows no teammates */
            }
        })();
    }, []);
    async function save() {
        if (!subject.trim())
            return;
        setBusy(true);
        try {
            // sentDate is a local calendar day; send it at local midday so the
            // reply window lands sensibly regardless of timezone.
            const sentAt = new Date(`${sentDate}T12:00`);
            // Custom mode reads from the free input (clamped 1–90); otherwise use
            // the selected preset.
            const effectiveWait = customWait
                ? Math.min(90, Math.max(1, customDays || 1))
                : waitDays;
            await api.post("/reply-tracker", {
                subject: subject.trim(),
                recipient: recipient.trim() || null,
                lead_id: leadId || null,
                sent_at: Number.isNaN(sentAt.getTime())
                    ? undefined
                    : sentAt.toISOString(),
                wait_days: effectiveWait,
                notes: notes.trim() || null,
                assignee_ids: assigneeIds,
            });
            onSaved();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't log the email");
            setBusy(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal crm-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: "\uD83D\uDCE8 Log a sent email" }), _jsx("p", { className: "muted small", children: "Track an email you're waiting on. We'll remind you in My Day if there's no reply within your wait window." }), _jsxs("div", { className: "crm-form-grid", children: [_jsxs("label", { className: "rt-field-wide", children: ["Subject / what it was about", _jsx("input", { value: subject, onChange: (e) => setSubject(e.target.value), placeholder: "e.g. Proposal for Winni \u2014 next steps", autoFocus: true })] }), _jsxs("label", { children: ["Sent to", _jsx("input", { value: recipient, onChange: (e) => setRecipient(e.target.value), placeholder: "name or email" })] }), _jsxs("label", { children: ["Attach to lead", _jsxs("select", { value: leadId, onChange: (e) => setLeadId(e.target.value), children: [_jsx("option", { value: "", children: "\u2014 None \u2014" }), leads.map((l) => (_jsx("option", { value: l.id, children: l.name }, l.id)))] })] }), _jsxs("label", { children: ["Date sent", _jsx("input", { type: "date", value: sentDate, onChange: (e) => setSentDate(e.target.value) })] }), _jsxs("label", { children: ["Nudge me after", _jsxs("select", { value: customWait ? "other" : String(waitDays), onChange: (e) => {
                                        if (e.target.value === "other") {
                                            setCustomWait(true);
                                        }
                                        else {
                                            setCustomWait(false);
                                            setWaitDays(Number(e.target.value));
                                        }
                                    }, children: [_jsx("option", { value: "1", children: "1 day" }), _jsx("option", { value: "2", children: "2 days" }), _jsx("option", { value: "3", children: "3 days" }), _jsx("option", { value: "5", children: "5 days" }), _jsx("option", { value: "7", children: "1 week" }), _jsx("option", { value: "14", children: "2 weeks" }), _jsx("option", { value: "other", children: "Other\u2026" })] })] }), customWait && (_jsxs("label", { children: ["Days to wait", _jsx("input", { type: "number", min: 1, max: 90, value: customDays, onChange: (e) => setCustomDays(Number(e.target.value)), placeholder: "e.g. 10", autoFocus: true })] })), _jsxs("div", { className: "rt-field-wide", children: [_jsxs("span", { className: "rt-picker-label", children: ["Assign to", " ", _jsx("span", { className: "muted small", children: "\u2014 only assigned people see it & get the nudge" })] }), coworkers.length === 0 ? (_jsx("span", { className: "muted small", children: "No teammates available." })) : (_jsx(MultiSelect, { options: coworkers.map((c) => ({ id: c.id, label: c.name })), values: assigneeIds, onChange: setAssigneeIds, placeholder: "Select people\u2026" })), assigneeIds.length === 0 && (_jsx("span", { className: "muted small rt-assignee-hint", children: "Heads up: with nobody assigned, this won't show in anyone's follow-up box." }))] }), _jsxs("label", { className: "rt-field-wide", children: ["Notes (optional)", _jsx("input", { value: notes, onChange: (e) => setNotes(e.target.value), placeholder: "anything to remember when you chase" })] })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: busy, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: save, disabled: busy || !subject.trim(), children: busy ? "Saving…" : "Track this email" })] })] }) }));
}
